import { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '@/state/gameStore';

const COLORS = ['#dc2626', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#ec4899'];
const PARTICLE_COUNT = 26;

interface ConfettiProps {
  /** Pass a freshly-incrementing key each time you want to retrigger. */
  trigger: string | number | null;
}

interface Particle {
  id: number;
  color: string;
  startX: number;
  endX: number;
  endY: number;
  rotate: number;
  duration: number;
  delay: number;
}

/**
 * CSS/framer-only confetti burst. No external library, respects reduceMotion
 * (in which case it renders nothing).
 *
 * Usage: render <Confetti trigger={someValueThatChangesOnEvent} /> in a
 * fixed-position container. When `trigger` changes, a fresh burst plays.
 */
export function Confetti({ trigger }: ConfettiProps) {
  const reduceMotion = useGameStore((s) => s.uiPrefs.reduceMotion);

  const particles = useMemo<Particle[]>(() => {
    if (trigger === null || trigger === undefined) return [];
    return Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
      id: i,
      color: COLORS[i % COLORS.length],
      startX: 0,
      endX: (Math.random() - 0.5) * 600,
      endY: 200 + Math.random() * 300,
      rotate: (Math.random() - 0.5) * 720,
      duration: 1.4 + Math.random() * 0.8,
      delay: Math.random() * 0.15,
    }));
  }, [trigger]);

  if (reduceMotion) return null;

  return (
    <AnimatePresence>
      {trigger !== null && trigger !== undefined && (
        <div
          key={String(trigger)}
          className="pointer-events-none fixed left-1/2 top-1/2 z-[60] -translate-x-1/2 -translate-y-1/2"
          aria-hidden
        >
          {particles.map((p) => (
            <motion.span
              key={p.id}
              initial={{ x: p.startX, y: 0, opacity: 1, rotate: 0, scale: 1 }}
              animate={{
                x: p.endX,
                y: p.endY,
                opacity: 0,
                rotate: p.rotate,
                scale: 0.6,
              }}
              transition={{ duration: p.duration, delay: p.delay, ease: 'easeOut' }}
              className="absolute h-2 w-2 rounded-sm"
              style={{ background: p.color }}
            />
          ))}
        </div>
      )}
    </AnimatePresence>
  );
}
